async function onBuyClicked() {
  addPaymentNetworkSimulation("Call delivery");
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Create the PaymentRequest object
  const supportedInstruments: PaymentMethodData[] = [
    {
      supportedMethods: "https://google.com/pay",
      data: {
        environment: "TEST",
        apiVersion: 2,
        apiVersionMinor: 0,
        merchantInfo: {
          merchantName: "Example Merchant",
        },
        allowedPaymentMethods: [
          {
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: [
                "AMEX",
                "DISCOVER",
                "INTERAC",
                "JCB",
                "MASTERCARD",
                "MIR",
                "VISA",
              ],
            },
            tokenizationSpecification: {
              type: "PAYMENT_GATEWAY",
              parameters: {
                gateway: "example",
                gatewayMerchantId: "exampleGatewayMerchantId",
              },
            },
          },
        ],
      },
    },
    {
      supportedMethods: "https://apple.com/apple-pay",
      data: {
        version: 3,
        merchantIdentifier: "org.webkit.demo",
        merchantCapabilities: [
          "supports3DS",
          "supportsCredit",
          "supportsDebit",
        ],
        supportedNetworks: ["visa", "amex"],
        countryCode: "FR",
      },
    },
  ];

  const details: PaymentDetailsInit = {
    total: {
      label: "TotalLabel",
      amount: { currency: "USD", value: "55.00" },
    },
    displayItems: [
      {
        label: "Original donation amount",
        amount: { currency: "USD", value: "65.00" },
      },
      {
        label: "Shipping",
        amount: { currency: "USD", value: "0.00" },
        pending: true,
      },
      {
        label: "Friends and family discount",
        amount: { currency: "USD", value: "-10.00" },
      },
    ],
  };

  const options: PaymentOptions = {
    requestPayerName: true,
    requestPayerPhone: true,
    requestPayerEmail: true,
    requestShipping: true,
  };

  const request = new PaymentRequest(
    supportedInstruments,
    details,
    options,
  );

  request.addEventListener("shippingaddresschange", (evt) => {
    console.log("onshippingaddresschange");
    evt.updateWith(updateAddress(details, request.shippingAddress));
  });

  request.addEventListener("shippingoptionchange", (evt) => {
    console.log("onshippingoptionchange");
    evt.updateWith(updateShipping(details, request.shippingOption));
  });

  // request.onmerchantvalidation = async (evt) => {
  //   console.log("onmerchantvalidation");
  //   evt.complete(merchantValidation(evt.validationURL));
  // };

  // Show popup, and wait for it to finish
  const instrumentResponse = await request.show();

  // Simulate a delay for payment submission
  addPaymentNetworkSimulation("Payment submission");
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Finish the payment loading
  await instrumentResponse.complete("success");

  setPaymentResult(JSON.stringify(instrumentResponse, null, 2));
}

async function updateShipping(details: PaymentDetailsUpdate, shippingOptionId: string | null) {
  if (!shippingOptionId) {
    throw new Error("Dont want to implement unselecting a shipping option");
  }

  console.log("updateAddress", details, shippingOptionId);

  const shippingOptions: PaymentShippingOption[] = details.shippingOptions ?? []

  shippingOptions.forEach((opt) => (opt.selected = false));

  // Find the select shipping option
  const shipping = shippingOptions.find(
    (opt) => opt.id === shippingOptionId,
  );

  if (shipping) {
    // Set the selected state on the found shipping option
    shipping.selected = true;
  }

  // Update the total
  if (details.total && details.displayItems && shipping) {
    // Update the displayed items in the command breakdown
    details.displayItems.splice(1, 1, shipping);

    details.total.amount.value = (55 + Number(shipping.amount.value)).toFixed(
      2,
    );
  }

  // Simulate a delay for total reevaluation submission
  addPaymentNetworkSimulation("Set shipping method");
  await new Promise((resolve) => setTimeout(resolve, 500));
  return details;
}

async function updateAddress(details: PaymentDetailsUpdate, shippingAddress: PaymentAddress | null) {
  if (!shippingAddress) {
    throw new Error("Dont want to implement unselecting an address");
  }

  console.log("updateAddress", details, shippingAddress);

  if (shippingAddress.country === "FR") {
    // Shipping if address is in FR
    details.shippingOptions = [
      {
        id: "frFreeShipping",
        label: "Free shipping in France",
        amount: { currency: "USD", value: "0.00" },
        selected: false,
      },
      {
        id: "frFastShipping",
        label: "Fast shipping in France",
        amount: { currency: "USD", value: "5.00" },
        selected: false,
      },
    ];

    delete details.error;
  } else if (shippingAddress.country === "US") {
    // Shipping if address is in the US
    details.shippingOptions = [
      {
        id: "usFreeShipping",
        label: "Free shipping in US",
        amount: { currency: "USD", value: "50.00" },
        selected: false,
      },
      {
        id: "usFastShipping",
        label: "Fast shipping in US",
        amount: { currency: "USD", value: "100.00" },
        selected: false,
      },
    ];

    delete details.error;
  } else {
    // Don't ship outside of US for the purposes of this example. Show error.
    details.error = "Cannot ship outside of US or FR.";
    delete details.shippingOptions;
  }

  // Simulate a delay for shipping address submission
  addPaymentNetworkSimulation("Set shipping address");
  await new Promise((resolve) => setTimeout(resolve, 500));
  return details;
}

async function merchantValidation(validationURL: string) {
  // Exclusive to Apple Pay

  // ----- THIS MUST BE SECURED ON THE SERVER SIDE, THIS IS JUST FOR THE DEMO -----
  // must implement the recuperation of an Apple Pay session on the backend using the merchant credentials.
  // https://github.com/WebKit/webkit/blob/master/Websites/webkit.org/demos/payment-request/merchant-validation.php

  const url = new URL(validationURL);
  if (!url.host.endsWith("apple.com")) {
    throw "Wrong validationUrl";
  }

  if (url.protocol.toLowerCase() !== "https") {
    throw "validationUrl not using https";
  }

  // Unfinished, really complicated.
  // Need the store account credentials, need to authenticate the server, need to make the request got from the server...
  // Use https://applepaydemo.apple.com/ to test the full behavior. onmerchantvalidation is the only thing missing.

  // const response = await fetch("http://example.com/api/merchantValidation", {
  //     body: { validationURL },
  //     method: "POST",
  // });

  addPaymentNetworkSimulation("Request merchantValidation");
  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    merchantIdentifier: "merchantIdentifier",
    domainName: "domainName",
    displayName: "displayName",
  };
}

function addPaymentNetworkSimulation(label: string) {
  const networkElement = document.getElementById("network");
  if (!networkElement) {
    throw new Error("Could not find networkElement")
  }
  networkElement.textContent += label + "\n";
}

function setPaymentResult(label: string) {
  const resultElement = document.getElementById("result");
  if (!resultElement) {
    throw new Error("Could not find resultElement")
  }
  resultElement.innerHTML = label;
}

// Init button
const payButton = document.getElementById("buyButton");
if (!payButton) {
  throw new Error("Could not find payButton")
}
payButton.addEventListener("click", onBuyClicked);